import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

const ADMIN_EMAIL = "admin@primalfeeders.com";

const AdminModal = ({ onClose }) => {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(ADMIN_EMAIL, pw);
      if (u.role !== "admin") { setErr("Not an admin account."); setLoading(false); return; }
      toast.success("Welcome back, Admin");
      onClose(); nav("/admin");
    } catch (e) {
      setErr(apiError(e.response?.data?.detail) || "Wrong password");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
        className="bg-[#0c0c0c] border border-[#222] w-full max-w-sm p-8" data-testid="admin-access-modal">
        <div className="flex justify-between items-center mb-6">
          <span className="overline inline-flex items-center gap-2"><Lock size={13} /> Admin Access</span>
          <button onClick={onClose} className="text-[#8A8782] hover:text-[#F4F1EC]"><X size={18} /></button>
        </div>
        <form onSubmit={submit}>
          <label className="overline">Admin password</label>
          <input data-testid="admin-pw-input" type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} className="field mb-4" />
          {err && <p data-testid="admin-pw-error" className="text-[#D94026] text-xs mb-3">{err}</p>}
          <button data-testid="admin-pw-submit" disabled={loading} className="btn-toxic w-full py-3 disabled:opacity-50">
            {loading ? "Verifying…" : "Enter Dashboard"}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export const Footer = () => {
  const [showAdmin, setShowAdmin] = useState(false);
  return (
    <footer className="border-t border-[#222] mt-32">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <span className="font-display text-4xl tracking-tight">Primal<span className="text-[#C7F000]">.</span></span>
          <p className="text-[#8A8782] text-sm mt-4 max-w-sm leading-relaxed">Live & frozen feeders, raised with obsessive standards. Shipped overnight to keepers who refuse to compromise.</p>
        </div>
        <div>
          <p className="overline mb-4">Catalog</p>
          <div className="flex flex-col gap-2 text-sm text-[#8A8782]">
            <Link to="/shop?category=insects" className="hover:text-[#F4F1EC] transition-colors">Insects</Link>
            <Link to="/shop?category=rodents" className="hover:text-[#F4F1EC] transition-colors">Rodents</Link>
            <Link to="/shop" className="hover:text-[#F4F1EC] transition-colors">All Feeders</Link>
          </div>
        </div>
        <div>
          <p className="overline mb-4">Account</p>
          <div className="flex flex-col gap-2 text-sm text-[#8A8782]">
            <Link to="/auth" className="hover:text-[#F4F1EC] transition-colors">Sign In</Link>
            <Link to="/orders" className="hover:text-[#F4F1EC] transition-colors">Order History</Link>
            <button data-testid="footer-admin-btn" onClick={() => setShowAdmin(true)} className="text-left inline-flex items-center gap-2 hover:text-[#C7F000] transition-colors"><Lock size={12} /> Admin</button>
          </div>
        </div>
      </div>
      <div className="border-t border-[#1a1a1a] py-6 text-center overline">© 2026 Primal Feeders — Acheta Domesticus & Beyond</div>
      <AnimatePresence>{showAdmin && <AdminModal onClose={() => setShowAdmin(false)} />}</AnimatePresence>
    </footer>
  );
};
